'use client'

import React, { HTMLProps, useState } from 'react'
import clsx from 'classnames'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { safePolygon, useDelayGroup, useFloating, useHover, useInteractions } from '@floating-ui/react'
import { Text } from '@/shared/ui'
import { IPerson } from '@/interfaces/persons'
import { Link } from '@/routing'
import style from './author.module.scss'

interface PhotoProps extends HTMLProps<HTMLDivElement> {
  authors?: IPerson[]
}

const AuthorItem = ({ author }: { author: IPerson }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
  })
  const { delay } = useDelayGroup(context)
  const hover = useHover(context, { delay, handleClose: safePolygon() })
  const { getReferenceProps, getFloatingProps } = useInteractions([hover])

  return (
    <Link
      className={clsx(style.wrapper, { [style.isOpen]: isOpen })}
      href={`/analytics?authorId=${author.id}`}
      {...getReferenceProps()}
      ref={refs.setReference}
    >
      <div className={style.authorWrapper}>
        {author.image?.url && (
          <div className={style.photo}>
            <Image src={author.image?.url} alt={author.name} fill style={{ objectFit: 'cover' }} />
          </div>
        )}
        <Text className={style.author} type="h2" color="dark" __html={author.name} />
      </div>
      <Text className={style.position} type="t2" color="dark-070" __html={author.position} />
      {isOpen && (
        <div className={style.tooltipContainer} ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
          <p
            className={clsx(style.tooltip, 'text text_type_d text_color_dark-040', {
              [style.isOpen]: isOpen,
            })}
          >
            {author.position}
          </p>
        </div>
      )}
    </Link>
  )
}

export const Author = (props: PhotoProps) => {
  const { className, authors } = props
  const translate = useTranslations()
  return (
    <>
      {authors?.length && authors?.length > 0 ? (
        <div className={clsx(style.container, className)}>
          <Text
            className={style.title}
            type="d"
            color="dark-040"
            __html={authors?.length && authors?.length > 1 ? translate('titleAuthors') : translate('titleAuthor')}
          />
          <div className={style.authors}>
            {authors.map(author => (
              <AuthorItem key={author.id ?? author.name} author={author} />
            ))}
          </div>
        </div>
      ) : (
        <></>
      )}
    </>
  )
}
